import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewRef } from '@angular/core';
import interact from 'interactjs';
import { Subscription } from 'rxjs';
import _ from 'lodash';
import { ColorPickerOptions } from '../../../../../../environments/configs/color-picker-options';
import { WysiwygOptions } from '../../../../../../environments/configs/wysiwyg-options';
import { environment } from '../../../../../../environments/environment';
import { Color } from '../../../../../models/color';
import { Page } from '../../../../../models/page';
import { Rect } from '../../../../../models/rect';
import { ApplicationStateService } from '../../../../../services/application-state.service';
import { AssetService } from '../../../../../services/asset.service';
import { BookFeatureActivationService } from '../../../../../services/book-feature-activation.service';
import { CommunicationService } from '../../../../../services/communication.service';
import { RectService } from '../../../../../services/rect.service';
import { BoundedInteractComponent } from '../../../../utils/bounded-interact-component';
import { NgStyle } from '@angular/common';

@Component({
    selector: 'app-page-rect',
    templateUrl: './page-rect.component.html',
    styleUrls: ['./page-rect.component.scss'],
    standalone: true,
    imports: [
        NgStyle
    ]
})
export class PageRectComponent extends BoundedInteractComponent implements OnInit, AfterViewInit, OnDestroy {

    private static defaultNoteSize = 260;
    private static minSize = 260;

    @Input() rect: Rect;
    @Input() page: Page;
    isMouseOver = false;
    crudPermitted = true;

    bookDataChangedSubscription: Subscription;
    zoomSubscription: Subscription;

    backgroundColor: string;
    noteColors: Array<Color>;
    summerNoteElement: any;
    options: object = WysiwygOptions.options;
    baseZIndex: number;

    resizeIndicatorIconURL: string;

    left: number;
    top: number;
    width = PageRectComponent.defaultNoteSize;
    height = PageRectComponent.defaultNoteSize;

    hasSmallHeight: boolean;
    debouncedOnResize: (() => void) & _.Cancelable;
    debouncedSave: ((skipMarkupFetching?: boolean) => void) & _.Cancelable;
    debouncedEnablePanning: (() => void) & _.Cancelable;
    thiz = this;

    pageContainer: any;
    preventAutoSaving = false;
    preventToggle = false;

    constructor(public applicationStateService: ApplicationStateService,
                public featureActivationService: BookFeatureActivationService,
                private communicationService: CommunicationService,
                private changeDetectorRef: ChangeDetectorRef,
                private assertService: AssetService,
                private rectService: RectService) {

        super();
        this.debouncedOnResize = _.debounce(this.onResize, 100);
        this.debouncedSave = _.debounce(this.saveContent, 1000);
        this.debouncedEnablePanning = _.debounce(this.enablePanning, 50);
        this.resizeIndicatorIconURL = this.assertService.getUrl('notes/resize-indicator.png');

        this.bookDataChangedSubscription = this.communicationService.bookFeatureDataChangedAnnouncedSource$.subscribe(event => {
            if (!(this.changeDetectorRef as ViewRef).destroyed && event.bookFeature instanceof Rect) {
                this.changeDetectorRef.detectChanges();
            }
        });
    }

    ngOnInit(): void {
        if (this.applicationStateService.isScrollMode()) {
            this.pageContainer = $(`.bookviewer-single-scroll-page-container[data-id=${this.page.id}]`);
        } else {
            this.pageContainer = $(`.bookviewer-single-page-container[data-id=${this.page.id}]`);
        }

        this.calculateOffset();
        // On small devices close notes.
        this.closeNoteOnSmallDevice();

        this.crudPermitted = environment.features.rectsActivated;
        this.setColor();
        this.noteColors = ColorPickerOptions.noteColors;
        this.baseZIndex = +this.rect.zIndex;
        this.preventAutoSaving = true;

        setTimeout(() => {
            this.initInteractSmallRect();
            this.initInteractExpanededRect();
        }, 50);

        // This should prevent saving elements after creation
        setTimeout(() => {
            this.preventAutoSaving = false;
        }, 1000);

        this.determineZIndex();
    }

    ngAfterViewInit(): void {
        // If the rect was just created toggle the Editor.
        if (this.rect && (this.rect as any).isNew) {
            (this.rect as any).isNew = undefined;
            this.saveContent();
        }
        if (this.rect.open && this.rect.id > 0) {
            this.initEditor();
        }
    }

    ngOnDestroy(): void {
        this.bookDataChangedSubscription.unsubscribe();
    }

    onResize(): void {
        // On small devices close notes.
        if (this.rect.open) {
            this.calculateOffset();
        }
    }

    delete(): void {

        this.rect.isDeleted = true;
        if (!this.rect.id) {
            this.rect = undefined;
        }

        this.rectService.deleteRect(this.rect)
            .then(() => {
                this.rect = undefined;
            });
    }

    stopEvent($event): void {
        if ($event.stopPropagation) {
            $event.stopPropagation();
        }
    }

    preventDefault($event): void {
        $event.preventDefault('always');
    }

    clickedOnRect(): void {
        this.rectService.logUsage(this.rect);
    }

    onSaveButtonClicked(): void {
        this.saveContent();
    }

    onMouseDown($event): void {
        this.applicationStateService.preventSwipe = true;
    }

    setColor(color?: Color): void {

        if (color) {
            this.rect.color = color;

            if (this.rect.id > 0) {
                this.rectService.updateRect(this.rect);

                return;
            }

            this.rectService.createRect(this.rect);

            return;
        }

        if (!this.rect.color) {
            this.rect.color = ColorPickerOptions.noteColors[1];
        }

        this.backgroundColor = this.rect.color.getColorString();
    }

    toggleIsExpanded(): void {
        if (this.preventToggle) {
            this.preventToggle = false;

            return;
        }

        this.rect.open = !this.rect.open;
        if (this.rect.open) {
            this.calculateOffset();
        } else {
            this.left = 0;
            this.top = 0;
        }

        this.resetCursor();
        this.saveContent();
        this.determineZIndex();
        if (this.rect.open) {
            this.preventAutoSaving = true;
            this.initEditor();
            this.setMarkup(this.rect.noteText);
            setTimeout(() => {
                this.preventAutoSaving = false;
            }, 50);
        }
    }

    createSummernoteEditor(): void {
        const id = `#rect-editor-${this.rect.id}`;
        this.summerNoteElement = $(id) as any;

        const editorContainer = $(`#point-rect-expanded-${this.rect.id}`);

        const containerHeight = editorContainer.height();
        const options = _.extend(WysiwygOptions.options, {});

        options.height = Math.max(containerHeight - 120);
        options.callbacks = {
            onChange: (contents, $editable) => {
                if (!this.rect.isDeleted && !this.preventAutoSaving) {
                    this.debouncedSave();
                }
            }
        };

        this.summerNoteElement.summernote(
            options
        );
    }

    getMarkup(): string {
        if (!this.summerNoteElement) {
            return this.rect.noteText;
        }

        return this.summerNoteElement.summernote('code');
    }

    setMarkup(markup: string): void {
        if (this.summerNoteElement && this.summerNoteElement.summernote) {
            this.summerNoteElement.summernote('code', markup);
        }
    }

    saveContent(skipMarkupFetching = false): void {

        if (!this.featureActivationService.rectsActivated) {
            return;
        }

        if (!skipMarkupFetching) {
            this.rect.noteText = this.getMarkup();
        }

        // Add current translation to the Rect.
        this.translateToRelative();

        if (this.rect.id > 0) {
            this.rectService.updateRect(this.rect);

            return;
        }

        this.rectService.createRect(this.rect);
    }

    resetCursor(): void {
        $('html')
            .css('cursor', 'unset');
    }

    /**
     * From http://interactjs.io/#resizing
     * Overwrite the default method, so the new position is stored in the note.
     * @param event
     */
    protected dragEvent(event): void {
        this.thiz.applicationStateService.preventPanning = true;
        const target = event.target;
        let translateX = event.dx;
        let translateY = event.dy;

        const container = $('.pages');
        const pageWidth = this.thiz.pageContainer.innerWidth();
        const pageHeight = this.thiz.pageContainer.innerHeight();

        if (container.length > 0) {

            const rect = target.getBoundingClientRect();
            translateX = Math.min(Math.max(container.offset().left - rect.left, event.dx),
                container.offset().left + container.width() - rect.right);
            translateY = Math.min(Math.max(container.offset().top - rect.top, event.dy),
                container.offset().top + container.height() - rect.bottom);
        }

        const lOffset = this.thiz.left / pageWidth * 100;
        const tOffset = this.thiz.top / pageHeight * 100;

        const xProcent = this.rect.position.x + lOffset + ((translateX * 100) / pageWidth);
        const yProcent = this.rect.position.y + tOffset + ((translateY * 100) / pageHeight);

        this.thiz.left = 0;
        this.thiz.top = 0;

        if (this.rect.open) {
            this.rect.position.x = Math.max(0, Math.min(xProcent, 100 - this.thiz.width / pageWidth * 100));
            this.rect.position.y = Math.max(0, Math.min(yProcent, 100 - this.thiz.height / pageHeight * 100));
        } else {
            const smallRectSizePx = 25;
            const smallRectSiteProcent = smallRectSizePx / pageWidth * 100;
            this.rect.position.x = Math.max(0, Math.min(xProcent, 100 - smallRectSiteProcent));
            this.rect.position.y = Math.max(0, Math.min(yProcent, 100 - smallRectSiteProcent));
        }

        this.thiz.debouncedSave(true);
    }

    /**
     * From http://interactjs.io/#resizing
     * Code was then adapted.
     * Calculates the size of the note.
     * @param event
     */
    protected resizeEvent(event): void {

        this.thiz.applicationStateService.preventPanning = true;
        event.stopPropagation();
        const target = event.target;

        let x = (parseFloat(target.getAttribute('data-x')) || 0);
        let y = (parseFloat(target.getAttribute('data-y')) || 0);

        const pageWidth = this.thiz.pageContainer.width();
        const pageHeight = this.thiz.pageContainer.height();
        const minSize = { width: PageRectComponent.minSize, height: PageRectComponent.minSize };

        const lOffset = this.thiz.left / pageWidth * 100;
        const tOffset = this.thiz.top / pageHeight * 100;

        this.rect.position.x += lOffset;
        this.rect.position.y -= tOffset;
        this.thiz.left = 0;
        this.thiz.top = 0;

        const zoomLevel = this.applicationStateService.zoomLevel;

        const maxSizeWidth = (pageWidth - this.rect.position.x / 100 * pageWidth - x) / zoomLevel;
        const maxSizeHeight = (pageHeight - this.rect.position.y / 100 * pageHeight - y) / zoomLevel;

        const maxSize = {
            width: maxSizeWidth,
            height: maxSizeHeight
        };

        const rectWidth = Math.max(minSize.width, Math.min(event.rect.width, maxSize.width));
        const rectHeight = Math.max(minSize.height, Math.min(event.rect.height, maxSize.height));

        // update the element's style
        this.thiz.width = rectWidth;
        this.thiz.height = rectHeight;
        this.rect.position.w = rectWidth / pageWidth * 100;
        this.rect.position.h = rectHeight / pageHeight * 100;

        // translate when resizing from top or left edges
        x += event.deltaRect.left;
        y += event.deltaRect.top;

        target.style.webkitTransform = target.style.transform =
            `translate(${x}px,${y}px)`;

        target.setAttribute('data-x', x);
        target.setAttribute('data-y', y);

        const editorContainer = $(target)
            .find('.note-editable');

        const editorPadding = 20;
        editorContainer.height(Math.max(WysiwygOptions.options.height - editorPadding, rectHeight - 140));

        this.thiz.debouncedSave(true);
    }

    private initEditor(): void {
        setTimeout(() => {
            this.createSummernoteEditor();
            this.setMarkup(this.rect.noteText);
        }, 100);
    }

    private determineZIndex(): void {
        if (this.rect.open) {
            this.rect.zIndex = this.baseZIndex + 2;
        } else {
            this.rect.zIndex = this.baseZIndex;
        }
    }

    /**
     * Calculate the width and height of the note and if necessary also the offset.
     */
    private calculateOffset(): void {

        const pageContainerWidth = this.pageContainer.width();
        const pageContainerHeight = this.pageContainer.height();

        const zoomLevel = this.applicationStateService.zoomLevel;

        // If the page container is small the regular limitations of width, height, top and left do not apply.
        if (pageContainerWidth < PageRectComponent.defaultNoteSize) {
            this.hasSmallHeight = true;
            const pagePositionContainer = $('.pages-position-container');
            this.height = +pagePositionContainer.height();
            this.top = -pageContainerHeight * (this.rect.position.y / 100);
            this.left = Math.min(pageContainerWidth -
                pageContainerWidth * (this.rect.position.x / 100) - this.width, 0) + 10;
            this.width = pagePositionContainer.width() - 10;
        } else {
            this.width = Math.min(
                pageContainerWidth,
                Math.max(PageRectComponent.minSize, pageContainerWidth * this.rect.position.w / 100 / zoomLevel));
            this.height = Math.min(
                pageContainerHeight,
                Math.max(PageRectComponent.minSize, pageContainerHeight * this.rect.position.h / 100 / zoomLevel));
            this.left = Math.min(
                pageContainerWidth - pageContainerWidth * (this.rect.position.x / 100) - this.width,
                0) / zoomLevel;
            this.top = Math.min(
                pageContainerHeight - pageContainerHeight * (this.rect.position.y / 100) - this.height,
                0) / zoomLevel;
        }
    }

    /**
     * Init interactivity for the small note element.
     */
    private initInteractSmallRect(): void {

        if (!this.featureActivationService.rectsActivated) {
            return;
        }

        const interactableSmallRect = interact(`#point-rect-small-${this.rect.id}`) as any;
        interactableSmallRect.rect = this.rect as object;
        interactableSmallRect.thiz = this;
        interactableSmallRect.draggable({
            onmove: event => {
                this.preventToggle = true;
                this.applicationStateService.preventPanning = true;
                const target = event.target;
                let translateX = event.dx;
                let translateY = event.dy;

                const container = $('.pages');
                const pageWidth = this.thiz.pageContainer.innerWidth();
                const pageHeight = this.thiz.pageContainer.innerHeight();

                if (container.length > 0) {

                    const rect = target.getBoundingClientRect();
                    translateX = Math.min(Math.max(container.offset().left - rect.left, event.dx),
                        container.offset().left + container.width() - rect.right);
                    translateY = Math.min(Math.max(container.offset().top - rect.top, event.dy),
                        container.offset().top + container.height() - rect.bottom);
                }

                const lOffset = this.thiz.left / pageWidth * 100;
                const tOffset = this.thiz.top / pageHeight * 100;

                const xProcent = this.rect.position.x + lOffset + ((translateX * 100) / pageWidth);
                const yProcent = this.rect.position.y + tOffset + ((translateY * 100) / pageHeight);

                this.thiz.left = 0;
                this.thiz.top = 0;

                if (this.rect.open) {
                    this.rect.position.x = Math.max(0, Math.min(xProcent, 100 - this.thiz.width / pageWidth * 100));
                    this.rect.position.y = Math.max(0, Math.min(yProcent, 100 - this.thiz.height / pageHeight * 100));
                } else {
                    const smallRectSizePx = 25;
                    const smallRectSiteProcent = smallRectSizePx / pageWidth * 100;
                    this.rect.position.x = Math.max(0, Math.min(xProcent, 100 - smallRectSiteProcent));
                    this.rect.position.y = Math.max(0, Math.min(yProcent, 100 - smallRectSiteProcent));
                }
                this.thiz.debouncedSave(true);
            }
        });
    }

    /**
     *  Init interactivity for the expaneded note element.
     */
    private initInteractExpanededRect(): void {

        if (!this.featureActivationService.rectsActivated) {
            return;
        }

        const interactableExpandedRect = interact(`#point-rect-expanded-${this.rect.id}`) as any;
        interactableExpandedRect.rect = this.rect;
        interactableExpandedRect.thiz = this;
        interactableExpandedRect.summernoteEditor = this.summerNoteElement;

        interactableExpandedRect.draggable({
            onmove: event => {
                this.applicationStateService.preventPanning = true;
                const target = event.target;
                let translateX = event.dx;
                let translateY = event.dy;

                const container = $('.pages');
                const pageWidth = this.thiz.pageContainer.innerWidth();
                const pageHeight = this.thiz.pageContainer.innerHeight();

                if (container.length > 0) {

                    const rect = target.getBoundingClientRect();
                    translateX = Math.min(Math.max(container.offset().left - rect.left, event.dx),
                        container.offset().left + container.width() - rect.right);
                    translateY = Math.min(Math.max(container.offset().top - rect.top, event.dy),
                        container.offset().top + container.height() - rect.bottom);
                }

                const lOffset = this.thiz.left / pageWidth * 100;
                const tOffset = this.thiz.top / pageHeight * 100;

                const xProcent = this.rect.position.x + lOffset + ((translateX * 100) / pageWidth);
                const yProcent = this.rect.position.y + tOffset + ((translateY * 100) / pageHeight);

                this.thiz.left = 0;
                this.thiz.top = 0;

                if (this.rect.open) {
                    this.rect.position.x = Math.max(0, Math.min(xProcent, 100 - this.thiz.width / pageWidth * 100));
                    this.rect.position.y = Math.max(0, Math.min(yProcent, 100 - this.thiz.height / pageHeight * 100));
                } else {
                    const smallRectSizePx = 25;
                    const smallRectSiteProcent = smallRectSizePx / pageWidth * 100;
                    this.rect.position.x = Math.max(0, Math.min(xProcent, 100 - smallRectSiteProcent));
                    this.rect.position.y = Math.max(0, Math.min(yProcent, 100 - smallRectSiteProcent));
                }

                this.thiz.debouncedSave(true);
            },
            ignoreFrom:
                [
                    `#point-rect-expanded-${this.rect.id} .note-content-wrapper`,
                    `#point-rect-expanded-${this.rect.id} .note-content-wrapper *`,
                    `#point-rect-expanded-${this.rect.id} .note-footer`,
                    `#point-rect-expanded-${this.rect.id} .note-footer *`
                ].join(', ')
        });

        interactableExpandedRect
            .resizable({
                edges: {
                    right: true,
                    bottom: true
                },
                onmove: event => this.resizeEvent(event),
                ignoreFrom: [
                    `#point-rect-expanded-${this.rect.id} .point-rect i`,
                    `#point-rect-expanded-${this.rect.id} .note-content-wrapper`,
                    `#point-rect-expanded-${this.rect.id} button`
                ].join(', ')
            });
    }

    /**
     * This method is used because transforming the translation from absolute to relative on every resize event causes
     * rounding errors.
     *
     * Takes the translate of the rect (e.g. when it was scaled left/top and calculates the relative
     * translation to the page. The relative translation is then added to the rects (relative position (x,y)) and
     * the absolute translation is then removed.
     */
    private translateToRelative(): void {

        const pageWidth = this.pageContainer.width();
        const pageHeight = this.pageContainer.height();
        const rectContainer = $(`#point-rect-expanded-${this.rect.id}`);
        const transX = +rectContainer.attr('data-x');
        const transY = +rectContainer.attr('data-y');

        if (transX > 0 || transX < 0 || transY > 0 || transY < 0) {
            this.rect.position.x = this.rect.position.x + (transX * 100) / pageWidth;
            this.rect.position.y = this.rect.position.y + (transY * 100) / pageHeight;
            rectContainer.css('transform', 'unset');
            rectContainer.attr('data-x', 0);
            rectContainer.attr('data-y', 0);
        }
    }

    /**
     * On small devices close all notes.
     */
    private closeNoteOnSmallDevice(): void {
        if (this.rect.open) {

            let pageContainer;
            if (this.applicationStateService.isScrollMode()) {
                pageContainer = $(`.bookviewer-single-scroll-page-container[data-id=${this.page.id}]`);
            } else {
                pageContainer = $(`.bookviewer-single-page-container[data-id=${this.page.id}]`);
            }
            if (pageContainer.width() < 380) {
                this.rect.open = false;
            }
        }
    }

    /**
     * Enables panning of the page.
     */
    private enablePanning(): void {
        this.thiz.applicationStateService.preventPanning = false;
    }
}
